import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    if (this.hasAttribute('manual-load-more')) {
      this.addEventListener('click', this.handleLoadMoreClick);
      this.loadMoreObserver = new MutationObserver(() => this.updateLoadMore());
      this.loadMoreObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy', 'data-batch-key'] });
      const gallery = this.querySelector('[ref="cardGallery"]');
      if (gallery) this.loadMoreRatioHelper = new PaginatedListAspectRatioHelper({ templateCard: gallery });
      this.updateLoadMore();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleLoadMoreClick);
    this.loadMoreObserver?.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  loadMoreBusy = false;
  loadMoreObserver;
  loadMoreRatioHelper;
  loadMoreStates = new WeakMap();

  getLoadMoreState(grid, controls) {
    let state = this.loadMoreStates.get(grid);
    const key = controls.dataset.batchKey;
    if (!state || state.key !== key) {
      state = { key, page: Number(controls.dataset.currentPage) };
      this.loadMoreStates.set(grid, state);
    }
    return state;
  }

  updateLoadMore() {
    const controls = this.querySelector('[data-load-more-controls]');
    const grid = this.refs.grid;
    if (!controls || !grid) return;
    const state = this.getLoadMoreState(grid, controls);
    const cards = [...grid.querySelectorAll(':scope > [data-product-id]')];
    const count = new Set(cards.map(card => card.dataset.productId)).size;
    const total = Number(controls.dataset.total);
    // Cross-collection pins may be outside the collection. Do not claim an
    // inaccurate combined total; progress still measures collection pages.
    const text = `SHOW ${count} OF ${total} PRODUCTS`;
    const label = controls.querySelector('[data-load-more-count]');
    if (label.textContent !== text) label.textContent = text;
    const progress = controls.querySelector('progress');
    const loaded = Math.min(total, Math.max(0, state.page * Number(controls.dataset.pageSize) - Number(controls.dataset.offset)));
    if (progress.value !== loaded) progress.value = loaded;
    const button = controls.querySelector('button');
    button.hidden = state.page >= Number(grid.dataset.lastPage);
    button.disabled = this.loadMoreBusy || this.getAttribute('aria-busy') === 'true';
  }

  handleLoadMoreClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-load-more-button]') : null;
    if (!button || this.loadMoreBusy || this.getAttribute('aria-busy') === 'true') return;
    const grid = this.refs.grid;
    const controls = this.querySelector('[data-load-more-controls]');
    if (!grid || !controls) return;
    const state = this.getLoadMoreState(grid, controls);
    const page = state.page + 1;
    if (page > Number(grid.dataset.lastPage)) return;
    const requestQuery = window.location.search;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    this.loadMoreBusy = true;
    button.textContent = 'LOADING...';
    this.updateLoadMore();
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      const currentControls = this.querySelector('[data-load-more-controls]');
      if (!this.isConnected || grid !== this.refs.grid || !currentControls ||
          this.getLoadMoreState(grid, currentControls) !== state || requestQuery !== window.location.search) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = doc.querySelector('[ref="grid"]');
      if (!incomingGrid) throw new Error('No product grid returned');
      const cards = [...incomingGrid.querySelectorAll(':scope > [ref="cards[]"]')];
      if (!cards.length) throw new Error('No products returned');
      const ids = new Set([...grid.querySelectorAll(':scope > [data-product-id]')].map(card => card.dataset.productId));
      const handles = new Set([...grid.querySelectorAll(':scope > [data-product-handle]')].map(card => card.dataset.productHandle));
      const additions = cards.filter(card => {
        if (ids.has(card.dataset.productId) || handles.has(card.dataset.productHandle)) return false;
        ids.add(card.dataset.productId);
        handles.add(card.dataset.productHandle);
        return true;
      });
      grid.append(...additions);
      // Advance even when every item in this page was already pinned.
      state.page = page;
      this.loadMoreRatioHelper?.processNewElements();
      for (const card of additions) customElements.upgrade(card);
      button.textContent = 'LOAD MORE';
    } catch (error) {
      console.error('Load more failed', error);
      button.textContent = 'TRY AGAIN';
    } finally {
      this.loadMoreBusy = false;
      if (button.textContent === 'LOADING...') button.textContent = 'LOAD MORE';
      this.updateLoadMore();
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
